/* =========================================================================
 * <DateFilter> — the ONE date control on every list surface.
 * [Jarvis · Claude] 2026-10-26 · model: src/lib/dateFilter.js
 *
 * Phone first (390px): the presets are a wrapping row of 44px chips, the
 * date inputs are native (the phone's own calendar wheel), and the summary
 * line under them always says, in words, which date is being searched, the
 * range WITH the year, how many matched, how many of those are projected,
 * and how many have no date — so nothing leaves the list without a sentence.
 *
 * Props
 *   value      { preset, from, to, undated }
 *   onChange   (next) => void
 *   label      the date this surface searches ("Event date", "Due date")
 *   summary    applyDateFilter(...).summary, for the counts line
 *   noun       what a row is called ("championships", "orders")
 * ========================================================================= */
import { PRESETS, describeRange, formatDay, selectDatePreset } from '../lib/dateFilter.js'
import styles from './DateFilter.module.css'

const n = (v) => Number(v || 0).toLocaleString()

export default function DateFilter({ value, onChange, label = 'Date', summary = null, noun = 'records', id = 'df', compact = false }) {
  const v = value || { preset: 'all', from: '', to: '', undated: 'include' }
  const set = (patch) => onChange?.({ ...v, ...patch })
  const range = summary?.range || null
  const undatedTotal = summary?.undatedTotal ?? 0
  const summaryText = summary ? (<>
          {v.undated === 'only'
            ? <>Showing the <strong>{n(summary.undatedShown)}</strong> {noun} with no {label.toLowerCase()}.</>
            : range
              ? <>
                  <strong>{n(summary.inRange)}</strong> {noun} with {/^[aeiou]/i.test(label) ? 'an' : 'a'} {label.toLowerCase()} {range.from && range.to && range.from === range.to ? 'on' : 'in'} {describeRange(range)}
                  {summary.projectedInRange ? <> · <span className={styles.projected}>{n(summary.projectedInRange)} projected, not confirmed</span></> : null}
                  {summary.undatedShown ? <> · plus <strong>{n(summary.undatedShown)}</strong> with no date, kept and flagged</> : null}
                  {summary.undatedHidden ? <> · {n(summary.undatedHidden)} with no date hidden</> : null}
                </>
              : v.preset === 'day' || v.preset === 'range'
                ? <>Pick a date to search. Every date includes its year.</>
                : <>Showing {n(summary.inRange + summary.undatedShown)} of {n(summary.total)} {noun}{summary.projectedInRange ? <> · {n(summary.projectedInRange)} projected, not confirmed</> : null}{summary.undatedShown ? <> · {n(summary.undatedShown)} with no {label.toLowerCase()}, kept and flagged</> : null}{summary.undatedHidden ? <> · {n(summary.undatedHidden)} with no {label.toLowerCase()} hidden</> : null}.</>}
  </>) : null

  const controls = (
    <section className={styles.wrap} aria-label={`Search by ${label.toLowerCase()}`} data-testid="date-filter">
      <div className={styles.head}>
        <span className={styles.title}>Search by {label.toLowerCase()}</span>
        {v.preset !== 'all' || v.undated !== 'include' ? (
          <button type="button" className={styles.clear} onClick={() => onChange?.({ preset: 'all', from: '', to: '', undated: 'include' })}>
            Clear dates
          </button>
        ) : null}
      </div>

      <div className={styles.chips} role="group" aria-label="Date presets">
        {PRESETS.map((p) => (
          <button key={p.key} type="button" aria-pressed={v.preset === p.key}
            className={`${styles.chip} ${v.preset === p.key ? styles.chipOn : ''}`}
            onClick={() => onChange?.(selectDatePreset(v, p.key))}>
            {p.label}
          </button>
        ))}
        <button type="button" aria-pressed={v.undated === 'only'}
          className={`${styles.chip} ${v.undated === 'only' ? styles.chipOn : ''}`}
          onClick={() => set({ undated: v.undated === 'only' ? 'include' : 'only' })}>
          No date{summary ? ` · ${n(undatedTotal)}` : ''}
        </button>
      </div>

      {(v.preset === 'day' || v.preset === 'range') && (
        <div className={styles.inputs}>
          <label className={styles.field} htmlFor={`${id}-from`}>
            <span>{v.preset === 'day' ? label : 'From'}</span>
            <input id={`${id}-from`} type="date" className={styles.input} value={v.from || ''}
              onChange={(e) => set({ from: e.target.value })} />
          </label>
          {v.preset === 'range' && (
            <label className={styles.field} htmlFor={`${id}-to`}>
              <span>To</span>
              <input id={`${id}-to`} type="date" className={styles.input} value={v.to || ''}
                onChange={(e) => set({ to: e.target.value })} />
            </label>
          )}
        </div>
      )}

      {summary && (
        <p className={styles.summary} data-testid="date-filter-summary">
          {summaryText}
        </p>
      )}
      {range && v.undated !== 'only' && summary?.undatedShown ? (
        <button type="button" className={styles.link} onClick={() => set({ undated: 'exclude' })}>
          Hide the {n(summary.undatedShown)} with no date
        </button>
      ) : null}
      {v.undated === 'exclude' ? (
        <button type="button" className={styles.link} onClick={() => set({ undated: 'include' })}>
          Show the {n(summary?.undatedHidden)} with no date again
        </button>
      ) : null}
    </section>
  )
  if (!compact) return controls
  // The Work queue keeps a result in the first screen. Every date control is
  // still reachable, and the active range/counts remain visible when closed.
  return <details className={styles.compact} data-testid="date-filter-disclosure">
    <summary>
      <span className={styles.compactLabel}>Search by {label.toLowerCase()}</span>
      <span className={styles.compactSummary}>{summaryText || 'All dates'}</span>
    </summary>
    {controls}
  </details>
}

/** "Oct 26, 2026 · projected" — the one way a filtered row prints its date. */
export function DateWithYear({ day, projected = false }) {
  if (!day) return <span className={styles.noDate}>No date</span>
  return (
    <span className={styles.dateTag}>
      {formatDay(day)}{projected ? <span className={styles.projected}> · projected</span> : null}
    </span>
  )
}
